import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { Slide } from '@/components/slides/Slide';
import { SLIDE_PREVIEW_SIZE } from '@/constant';
import { useMedia } from '@/hooks/useMedia/useMedia';
import { QuestionSlide } from '@/types/slide';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { formatName } from '@/utils/name';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import TimerIcon from '@mui/icons-material/Timer';
import { Box, Collapse, Dialog, DialogContent, DialogProps, IconButton, Paper, Stack, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableRow, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideResult } from '../../../hooks/types/result';
import { formatDuration } from '../format';
import { ResultsDisplay } from './ResultsDisplay';
import { ResultsSummaryCard } from './ResultsSummaryCard';

function Row(props: { slide: QuestionSlide; name: string }) {
    const { slide, name } = props;
    const { t } = useTranslation();
    const { isMobile } = useMedia();
    const [open, setOpen] = React.useState(true);

    const timeSpent = useMemo(
        () =>
            formatDuration(
                slide.questions.reduce((total, question) => {
                    return total + question.answers.reduce((total, answer) => total + (new Date(answer.endTime).getTime() - new Date(answer.startTime).getTime()), 0);
                }, 0) / 1000,
            ),
        [slide.questions],
    );

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    [`& .${tableCellClasses.root}`]: {
                        borderBottom: 'none',
                    },
                    ':hover': {
                        cursor: 'pointer',
                    },
                }}
                hover
                onClick={() => setOpen(!open)}
            >
                <TableCell component='th' scope='row' sx={{ width: '60%' }}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <InitialalsAvatar
                            sx={{
                                bgcolor: generateAvatarColor(name)?.bg,
                                color: generateAvatarColor(name)?.text,
                            }}
                        >
                            {getInitials(name)}
                        </InitialalsAvatar>
                        <Typography variant='body2'>{name}</Typography>
                    </Stack>
                </TableCell>
                <TableCell sx={{ width: '20%' }}>
                    {slide.score} / {slide.questions.length}
                </TableCell>
                <TableCell align='right' sx={{ width: '20%' }}>
                    <IconButton aria-label='expand row' size='small' onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ padding: 0 }} colSpan={6}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <Stack spacing={2} direction={isMobile ? 'column' : 'row'} sx={{ py: 2 }}>
                            <Stack spacing={2} sx={{ alignItems: 'center' }}>
                                <div
                                    style={{
                                        height: SLIDE_PREVIEW_SIZE.height,
                                        width: SLIDE_PREVIEW_SIZE.width,
                                    }}
                                >
                                    <Slide components={slide.data} questions={slide.questions} preview={{ answer: true }} />
                                </div>
                                <Box sx={{ px: 2 }}>
                                    <Typography>
                                        {t('result.label.timeSpent')}: {timeSpent}
                                    </Typography>
                                </Box>
                            </Stack>
                            {slide.questions.length > 0 && (
                                <Box sx={{ overflow: 'auto' }}>
                                    <ResultsDisplay slide={slide} />
                                </Box>
                            )}
                        </Stack>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

/**
 * Display the result per slide
 * @param slides - The slides to display
 * @returns -
 */
function CollapsibleResultsTable({ slides }: { slides: (QuestionSlide & { student: { id: string; familyName: string; givenName: string; studentNumber: string } })[] }) {
    return (
        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
            <Table aria-label='collapsible table' sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                    {slides.map((slide) => (
                        <Row
                            key={slide.student.id}
                            slide={slide}
                            name={`${formatName(slide.student.familyName, slide.student.givenName)}` + (slide.student.studentNumber ? ` (${slide.student.studentNumber})` : '')}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export const SlideResultsDialog: React.FC<DialogProps & { result: SlideResult & { index: number } }> = ({ result, ...props }) => {
    const { t } = useTranslation();
    const { isMobile } = useMedia();

    return (
        <Dialog fullScreen sx={() => (isMobile ? { '& .MuiDialog-paper': { borderRadius: 0 } } : { px: 4, py: 6 })} {...props}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ mx: 3, my: 2 }}>
                    <Typography variant='caption' sx={{ fontSize: 14 }}>
                        {t('result.label.slide')}
                    </Typography>
                    <Typography variant='h4'>{`${t('result.label.slide')} (${result.index + 1})`}</Typography>
                </Box>
                <IconButton aria-label='close' onClick={(event) => props.onClose && props.onClose(event, 'backdropClick')} sx={{ m: 2, height: 40, width: 40 }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent>
                <Stack spacing={2}>
                    <Grid container rowSpacing={1} columnSpacing={1} columns={{ xs: 6, sm: 6, md: 12 }}>
                        <Grid size={6}>
                            <ResultsSummaryCard icon={<PieChartIcon />} title={t('result.label.averageScore')} value={`${result.averageScore.toFixed(1)} / ${result.questionCount}`} />
                        </Grid>
                        <Grid size={6}>
                            <ResultsSummaryCard icon={<TimerIcon />} title={t('result.label.averageTimeSpent')} value={formatDuration(result.averageTimeSpent / 1000)} />
                        </Grid>
                    </Grid>
                    <CollapsibleResultsTable slides={result.slides} />
                </Stack>
            </DialogContent>
        </Dialog>
    );
};
